import base64 from 'base-64'
import createHmac from 'create-hmac'
import CryptoJS from 'crypto-js'
import SparkMD5 from 'spark-md5'
import auth from './auth'
import device from './device'
import store from '../store'
import { getLocale } from 'redux-polyglot'

const CryptoJSAesJson = {
  stringify: function(cipherParams) {
    const j = {
      ct: cipherParams.ciphertext.toString(CryptoJS.enc.Base64)
    }
    if (cipherParams.iv) {
      j.iv = cipherParams.iv.toString()
    }
    if (cipherParams.salt) {
      j.s = cipherParams.salt.toString()
    }
    return JSON.stringify(j)
  },
  parse: function(jsonStr) {
    const j = JSON.parse(jsonStr)
    const cipherParams = CryptoJS.lib.CipherParams.create({
      ciphertext: CryptoJS.enc.Base64.parse(j.ct)
    })
    if (j.iv) {
      cipherParams.iv = CryptoJS.enc.Hex.parse(j.iv)
    }
    if (j.s) {
      cipherParams.salt = CryptoJS.enc.Hex.parse(j.s)
    }
    return cipherParams
  }
}

const widgetTklData =
  typeof widget !== 'undefined' && widget.preferences
    ? widget.preferences.getItem('TKLDATA')
    : null
const encrypted =
  window.TKLDATA !== undefined
    ? window.TKLDATA
    : widgetTklData !== null
    ? widgetTklData
    : undefined
const decrypted =
  typeof encrypted !== 'undefined'
    ? JSON.parse(
        CryptoJS.AES.decrypt(
          encrypted,
          'SLRBuvuaDzCpNEbZR4rhXJU8ENXgS6SvHZtHkXe92CQwLKbU',
          { format: CryptoJSAesJson }
        ).toString(CryptoJS.enc.Utf8)
      )
    : {}

const API_ROOT = decrypted.API_ROOT
const API_PATH = decrypted.API_PATH
const API_APP_ID = decrypted.API_APP_ID
const API_APP_KEY = decrypted.API_APP_KEY
const API_TIMEZONE = decrypted.API_TIMEZONE
const DEVICE_GENERIC_KEY = decrypted.DEVICE_GENERIC_KEY
const DEVICE_HISENSE_KEY = decrypted.DEVICE_HISENSE_KEY

const apiCall = (endpoint, method = 'GET', body = {}) => {
  const init = {}

  const apiAuthKey = auth.getAuthKey()

  let authMethod = method
  if (method === 'POST' && typeof body['method'] !== 'undefined') {
    authMethod = body.method
  }
  init.method = authMethod

  if (authMethod !== 'GET' && Object.keys(body).length !== 0) {
    init.body = new FormData()
    for (const key in body) {
      init.body.append(key, body[key])
    }
  }

  const requestUri = `/${API_PATH}${API_APP_ID}${endpoint}`
  const signKey = `${authMethod.toUpperCase()}:${requestUri}:${apiAuthKey}`

  const password = createHmac('sha256', new Buffer(API_APP_KEY))
    .update(signKey)
    .digest('hex')

  init.headers = {
    Authorization: 'Basic ' + base64.encode(apiAuthKey + ':' + password),
    'X-Suppress-Response-Codes': 'true'
  }

  return fetch(API_ROOT + requestUri, init)
}

const getNotifyDeviceArguments = () => {
  const args = {
    id: device.getId(),
    name: device.getName(),
    mac: device.getMacAddress(),
    type: device.getType(),
    serial_number: device.getSerialNumber()
  }

  if (args.type === 'hisense') {
    args.serial_number_type = 'tv-v2-hisense'
    args.serial_number_hash = SparkMD5.hash(
      `${DEVICE_HISENSE_KEY}-${args.serial_number}-${args.name}`
    )
  } else {
    args.serial_number_type = 'tv-v2-generic'
    args.serial_number_hash = SparkMD5.hash(
      `${DEVICE_GENERIC_KEY}-${args.serial_number}-${args.name}`
    )
  }

  return args
}

const getQueryString = () => {
  let query = [`lang=${getLocale(store.getState())}`]
  if (API_TIMEZONE) {
    query.push(`tz=${API_TIMEZONE}`)
  }
  // query.push(`ck=${Date.now()}`)
  return query.join('&')
}

const isWebOS = () => !!(window && window.PalmSystem);
const isTizen = () => typeof tizen !== 'undefined' && tizen.application;
// const isHisense = () => typeof window.Hisense_GetDeviceID === 'function';

const getDeviceFilter = () => {
  if (isWebOS()) {
    return 'lg_app_available=1';
  }

  if (isTizen()) {
    return 'samsung_app_available=1';
  }

  return 'tvv2_app_available=1';
}

const api = {
  ready: () => typeof API_ROOT === 'string',

  /**
   * API methods related to retrieving application settings
   */

  getLanguages: () => apiCall('/settings/languages'),

  getTranslations: () =>
    apiCall(
      `/settings/dictionary?sections[]=checkout_app&sections[]=support_app&sections[]=tv_account&sections[]=tv_app&sections[]=tv_auth&sections[]=tv_channels&sections[]=tv_dvr&sections[]=tv_extra&sections[]=tv_media&sections[]=tv_paywall&sections[]=tv_recommendations&sections[]=tv_search&sections[]=tv_settings&sections[]=tv_support&sections[]=tv_video_library&sections[]=tv_youtube&${getQueryString()}`
    ),

  getSettings: () => apiCall(`/settings?${getQueryString()}`),

  /**
   * API methods related to recommendations route
   */

  getRecommendations: () => apiCall(`/recommendations?${getQueryString()}`),

  /**
   * API methods related to channels app
   */

  getChannel: id =>
    apiCall(`/channels/${id}?${getDeviceFilter()}&${getQueryString()}`),

  getChannels: (socialBroadcasters = false) => {
    const typeParam = socialBroadcasters
      ? 'social_broadcasts'
      : 'premium_broadcasts'
    const url = `/channels?mobile_streams_only=1&order_by=stb_number&direction=asc&limit=1000&channel_type=all&include_tveverywhere_channels=0&include_sling_channels=0&${typeParam}=1&${getDeviceFilter()}&${getQueryString()}`
    return apiCall(url)
  },

  getFeaturedChannels: () => {
    const url = `/channels?mobile_streams_only=1&order_by=featured&direction=asc&limit=1000&channel_type=all&include_tveverywhere_channels=0&include_sling_channels=0&premium_broadcasts=1&${getDeviceFilter()}&featured=1&${getQueryString()}`
    return apiCall(url)
  },

  getPrograms: (from, to, socialBroadcasters = false) => {
    const typeParam = socialBroadcasters
      ? 'social_broadcasts'
      : 'premium_broadcasts'
    const url = `/channel-events?from=${from}&to=${to}&channel_type=all&include_tveverywhere_channels=0&include_sling_channels=0&${typeParam}=1&${getDeviceFilter()}&${getQueryString()}`
    return apiCall(url)
  },

  getChannelPackages: channelId =>
    apiCall(`/channels/${channelId}/upgrade-options?${getQueryString()}`),
  getPremiumDetails: channelId =>
    apiCall(`/premium-channels/${channelId}?${getQueryString()}`),
  getPreviewUrl: channelId =>
    apiCall(
      `/channels/${channelId}/preview-url?device_id=${device.getId()}&${getQueryString()}`
    ),

  /**
   * API methods related to radios
   */
  getRadio: id => apiCall(`/radios/${id}?${getQueryString()}`),

  getRadios: (socialBroadcasters = false) => {
    const typeParam = socialBroadcasters
      ? 'social_broadcasts'
      : 'premium_broadcasts'
    const url = `/radios?order_by=name&direction=asc&limit=1000&${typeParam}=1&${getDeviceFilter()}&${getQueryString()}`
    return apiCall(url)
  },

  /**
   * API methods related to device management
   */

  notifyDevice: () =>
    apiCall(
      `/devices/notify?${getQueryString()}`,
      'POST',
      getNotifyDeviceArguments()
    ),

  notifyLogout: () =>
    apiCall(`/devices/logout?${getQueryString()}`, 'POST', {
      id: device.getId()
    }),

  /**
   * API methods related to user authentication
   */

  authenticate: (username, password) => {
    if (username.indexOf('@') === -1) {
      return apiCall(`/authenticate?${getQueryString()}`, 'POST', {
        username: username,
        password: SparkMD5.hash(password)
      })
    } else {
      return apiCall(`/authenticate/email?${getQueryString()}`, 'POST', {
        email: username,
        password: SparkMD5.hash(password)
      })
    }
  },

  getUserDetails: () => apiCall(`/me?${getQueryString()}`),

  register: (username, email, password) =>
    apiCall(`/register?${getQueryString()}`, 'POST', {
      username: username,
      email: email,
      password: password
    }),

  /**
   * API methods related to Checkout app
   */

  getAccountDetails: () => apiCall(`/me?${getQueryString()}`),

  getCurrentSubscriptions: () =>
    apiCall(`/me/subscriptions?${getQueryString()}`),

  getPackages: () =>
    apiCall(`/plans/standard?${getDeviceFilter()}&${getQueryString()}`),

  getPackage: packageId =>
    apiCall(`/plans/${packageId}/standard?${getQueryString()}`),

  getPremiumAddons: () => apiCall(`/premium-channels?${getQueryString()}`),

  getAddonDetails: channelId =>
    apiCall(`/premium-channels/${channelId}?${getQueryString()}`),

  getAddonVideos: (channelId, limit = 100) =>
    apiCall(`/channels/${channelId}/videos?limit=${limit}&${getQueryString()}`),

  getPackageDeviceAddons: packageId =>
    apiCall(
      `/addon-devices?group_id=${packageId}&offset=0&limit=999&order_by=devices_number&direction=ASC&${getQueryString()}`
    ),

  getUserDeviceAddons: () =>
    apiCall(
      `/addon-devices/user?offset=0&limit=999&order_by=devices_number&direction=ASC&${getQueryString()}`
    ),

  addItemToCart: (referenceId, itemId, itemType) =>
    apiCall(`/carts/${referenceId}/items?${getQueryString()}`, 'POST', {
      item_id: itemId,
      type: itemType
    }),

  getCartDetails: referenceId =>
    apiCall(`/carts/${referenceId}?${getQueryString()}`),

  getCartItems: referenceId =>
    apiCall(`/carts/${referenceId}/items?${getQueryString()}`),

  removeCartItem: (referenceId, id) =>
    apiCall(`/carts/${referenceId}/items/${id}?${getQueryString()}`, 'POST', {
      method: 'DELETE'
    }),

  checkout: (referenceId, data) =>
    apiCall(`/carts/${referenceId}/checkout?${getQueryString()}`, 'POST', data),

  payOrder: (orderId, data) =>
    apiCall(`/orders/${orderId}?${getQueryString()}`, 'POST', data),

  /**
   * API methods related to DVRs
   */

  getDvrs: () =>
    apiCall(
      `/me/dvr?offset=0&limit=999&order_by=start&direction=DESC&status=all&device_id=${device.getId()}&${getQueryString()}`
    ),

  getDvrList: () =>
    apiCall(
      `/me/dvr?offset=0&limit=999&status=all&shrinked=1&device_id=${device.getId()}&${getQueryString()}`
    ),

  getDvr: dvrId =>
    apiCall(`/me/dvr/${dvrId}?device_id=${device.getId()}&${getQueryString()}`),

  removeDvr: dvrId =>
    apiCall(`/me/dvr/${dvrId}?${getQueryString()}`, 'POST', {
      method: 'DELETE'
    }),

  scheduleDvr: programId =>
    apiCall(`/me/dvr?${getQueryString()}`, 'POST', {
      event_id: programId
    }),
  /**
   * API methods related to Video Library
   */

  getPopularVideos: () =>
    apiCall(
      `/videos?order_by=popularity&direction=desc&limit=12&with[]=description&${getQueryString()}`
    ),

  getRecentVideos: () =>
    apiCall(
      `/videos?order_by=created&direction=desc&limit=12&with[]=description&${getQueryString()}`
    ),

  getTopLevelCategories: () => apiCall(`/video-categories?${getQueryString()}`),

  getCategoryParents: categoryId =>
    apiCall(`/video-categories/${categoryId}/parents?q=a&${getQueryString()}`),

  getCategoryItems: categoryId =>
    apiCall(
      `/video-categories/${categoryId}/items?limit=24&order_by=name&direction=ASC&${getQueryString()}`
    ),

  getCategoryVideos: (categoryId, offset = 0, limit = 24) =>
    apiCall(
      `/video-categories/${categoryId}/videos?offset=${offset}&limit=${limit}&order_by=name&direction=ASC&with[]=description&${getQueryString()}`
    ),

  getVideo: (videoId, width, height, appType, deviceMake, appBundle) =>
    apiCall(
      `/videos/${videoId}?embed_width=${width}&embed_height=${height}&device_ifa=${device.getUuid()}&app_type=${appType}&device_make=${deviceMake}&app_bundle=${appBundle}&ssai_workaround=1&${getQueryString()}`
    ),

  /**
   * API methods related to subscription list in account screen
   */

  getSubscriptionDetails: () => apiCall(`/subscriptions?${getQueryString()}`),

  getOneTimePurchasesDetails: () =>
    apiCall(`/subscriptions/one-time?${getQueryString()}`),

  cancelSubscriptionItem: (type, id) =>
    apiCall(`/subscriptions/${type}/${id}?${getQueryString()}`, 'POST', {
      method: 'DELETE'
    }),

  cancelSubscription: () =>
    apiCall(`/subscriptions?${getQueryString()}`, 'POST', {
      method: 'DELETE'
    }),

  /**
   * API methods related to device list in account screen
   */

  getDevices: () =>
    apiCall(`/me/devices?order_by=updated&direction=DESC&${getQueryString()}`),

  removeDevice: id =>
    apiCall(`/me/devices/${id}?${getQueryString()}`, 'POST', {
      method: 'DELETE'
    }),

  getBillingDetails: () => apiCall(`/me/billing?${getQueryString()}`),

  updateBillingDetails: billingDetails =>
    apiCall(`/me/billing?${getQueryString()}`, 'POST', billingDetails),

  /**
   * API methods related to search
   */

  search: keyword =>
    apiCall(
      `/search?q=${encodeURIComponent(
        keyword
      )}&in[]=channels&in[]=videos&${getDeviceFilter()}&limit=25&device_id=${device.getId()}&${getQueryString()}`
    ),

  getSuggestedKeywords: keyword =>
    apiCall(
      `/search/suggested?q=${encodeURIComponent(keyword)}&${getQueryString()}`
    ),

  getTrendingKeywords: () => apiCall(`/search/trending?${getQueryString()}`),

  getRecentKeywords: () =>
    apiCall(`/search/recent?device_id=${device.getId()}&${getQueryString()}`),

  /**
   * API methods related to YouTube content
   */
  getYouTubeContent: () => apiCall(`/youtube?${getQueryString()}`),

  /**
   * API methods related to Support Tickets
   */
  getTopics: () =>
    apiCall(
      `/faq/topics?order_by=topic_order&direction=asc&${getQueryString()}`
    ),

  getTopicQuestions: topicId =>
    apiCall(
      `/faq/questions?filter_by=topic_id&id=${topicId}&${getQueryString()}`
    ),

  getQuestions: () => apiCall(`/faq/questions'+ getQueryString(false)`),

  getQuestion: questionId =>
    apiCall(`/faq/${questionId}/question?${getQueryString()}`),

  getPages: () => apiCall(`/faq/pages?${getQueryString()}`),

  getPage: pageId => apiCall(`/faq/${pageId}/pages?${getQueryString()}`),

  getTickets: () =>
    apiCall(`/support-tickets?order_by=last_comment_timestamp&direction=DESC`),

  getTicket: id => apiCall(`/support-tickets/${id}`),

  getTicketsTypes: () => apiCall(`/support-tickets/types`),

  postNewTicket: (title, message) =>
    apiCall(`/support-tickets`, 'POST', {
      title: title,
      description: message
    }),
  postReplyLog: (id, message) =>
    apiCall(`/support-tickets/${id}/reply`, 'POST', {
      comment_type: 'external',
      comment: message
    }),

  /**
   * API methods related to Privacy Policy
   */
  getPrivacyPolicy: () =>
    apiCall(`/settings/page/privacy-policy?${getQueryString()}`),

  /**
   * API methods related to load list of HTML5 Games
   */
  getGames: () => apiCall(`/appapp?filter_by=html5_game&${getQueryString()}`),

  /**
   * API methods related to load list of HTML5 Apps
   */
  getApps: () => apiCall(`/appapp?filter_by=html5_app&${getQueryString()}`),

  /**
   * API methods related to load list of radios
   */
  getCustomRadios: () => apiCall(`/custom-radios?${getQueryString()}`)

}

export default api
